import { useGlobalDependencyProvider } from '@teachino/shared-util';

/* eslint-disable @typescript-eslint/naming-convention */
type RefreshTokenResponse = {
  access_token: string;
  refresh_token: string;
};
export function useAuth() {
  const di = useGlobalDependencyProvider();
  const config = di.inject('config');

  const UNAUTHENTICATED_ERROR_CODE = 401;

  async function fetchWithAuth(
    ...params: Parameters<typeof fetch>
  ): ReturnType<typeof fetch> {
    const [input, init = {}] = params;

    const headers = new Headers(init.headers);
    const token = localStorage.getItem('token');
    if (!token) {
      return fetch(input, { ...init, headers });
    }

    headers.set('Authorization', token);

    return fetch(input, { ...init, headers }).then((response) => {
      if (response.status === UNAUTHENTICATED_ERROR_CODE) {
        return refreshAuth().then(() => {
          headers.set('Authorization', localStorage.getItem('token') ?? '');
          return fetch(input, { ...init, headers });
        });
      }
      return response;
    });
  }

  async function refreshAuth(): Promise<void> {
    return fetch(`${config.baseUrl()}/auth/refresh-token`, {
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify({
        refreshToken: getRefreshToken(),
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response) => {
      if (response.ok) {
        return response.json().then((data) => {
          if (isObjectWithToken(data)) {
            setRefreshToken(data.refresh_token);
            localStorage.setItem('token', `Bearer ${data.access_token}`);
          } else {
            throw new Error('Malformed refresh token response');
          }
        });
      } else {
        throw new Error('Failed to refresh token');
      }
    });
  }
  function isObjectWithToken(input: unknown): input is RefreshTokenResponse {
    return (
      input !== null &&
      typeof input === 'object' &&
      'access_token' in input &&
      typeof input.access_token === 'string' &&
      'refresh_token' in input &&
      typeof input.refresh_token === 'string'
    );
  }
  function setRefreshToken(token: string): void {
    localStorage.setItem('refreshToken', token);
  }
  function getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  function getJwt(): string | null {
    return localStorage.getItem('token');
  }

  return {
    fetchWithAuth,
    getRefreshToken,
    refreshAuth,
    getJwt,
    setRefreshToken,
  };
}
